import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as React from "react";
import ConfirmDialog from "../container/ConfirmDialog/ConfirmDialog";
import Edit from "../container/Edit";
import { AppContext } from "../helpers/contexts/AppContext";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import AddCashOutOption from "./AddCashOutOption";
import { enqueueSnackbar } from "notistack";

export default function CashOutOptions() {
  const endpoint = "cashout-option/all";
  const [cashoutOptions, setCashoutOptions] = React.useState([]);

  const { REACT_APP_ACCOUNTS_API_URL } = process.env;
  const axios = useAxiosPrivate();
  const skipStart = 0;
  const skipEnd = -1;
  const displayStart = 0;
  const displayEnd = -1;
  const nonUpdatableFields = ["id"];

  const {
    openConfirmDialog,
    handleOpenConfirmDialog,
    handleCloseConfirmDialog,
    handleCloseEditDialog,
    handleOpenEditDialog,
    openEditDialog,
    edit,
    CustomToolbar,
    auth,
  } = React.useContext(AppContext);

  const [confirmMessage, setConfirmMessage] = React.useState("");
  const [dialogHeader, setDialogHeader] = React.useState("");
  const [action, setAction] = React.useState(null);
  const [actionPresent, setactionPresent] = React.useState(false);
  const [payload, setPayload] = React.useState(null);

  const [editDialogHeader, setEditDialogHeader] = React.useState("");
  const [editActionPresent, setEditActionPresent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [param, setParam] = React.useState(null);
  const [editRow, setEditRow] = React.useState({});

  const deleteCashOutOption = (id) => {
    axios
      .delete(`${REACT_APP_ACCOUNTS_API_URL}/${endpoint}/${id}`)
      .then((res) => {
        if (res.status && res.status === 200) {
          setCashoutOptions(cashoutOptions.filter((co) => co.id !== id));
          enqueueSnackbar("Cashout option deleted successfully.", {
            variant: "success",
          });
        }
      })
      .catch((err) => {
        return enqueueSnackbar(`${err.response.data.apiErrors[0].cause}`, {
          variant: "error",
        });
      });
  };

  const handleEdit = (e, params) => {
    e.stopPropagation(); // don't select this row after clicking

    const api = params.api;
    const thisRow = {};

    api
      .getAllColumns()
      .filter((c) => c.field !== "__check__" && !!c)
      .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));

    setEditRow(thisRow);
    setEditDialogHeader("Update Roles Details");
    setEditActionPresent(true);
    setAction("edit");
    setParam(thisRow.id);
    handleOpenEditDialog();
  };

  const handleDelete = (e, params) => {
    e.stopPropagation(); // don't select this row after clicking

    const api = params.api;
    const thisRow = {};

    api
      .getAllColumns()
      .filter((c) => c.field !== "__check__" && !!c)
      .forEach((c) => (thisRow[c.field] = params.getValue(params.id, c.field)));
    setConfirmMessage(
      `Are you sure you want to delete Cashout Option ${thisRow.option_name}?`
    );
    setDialogHeader("Delete Cashout Option");
    setactionPresent(true);
    setAction("delete");
    setPayload(thisRow.id);
    handleOpenConfirmDialog();
  };

  const actions = [
    {
      name: "edit",
      onclick: (e, params) => handleEdit(e, params),
      icon: <EditIcon />,
      color: "primary",
      role: "EDIT_CASHOUT_OPTION",
    },
    {
      name: "delete",
      onclick: (e, params) => handleDelete(e, params),
      icon: <DeleteIcon />,
      color: "warning",
      role: "DELETE_CASHOUT_OPTION",
    },
  ];

  const columns = [
    { field: "id", headerName: "ID", width: 50 },
    { field: "option_name", headerName: "Name", width: 100 },
    { field: "account", headerName: "Collection Account", width: 150 },
    { field: "url", headerName: "Url", width: 300 },
    {
      field: "action",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => {
        return actions.map((action) =>
          auth?.permissions?.find(
            (perm) => action.role.includes(perm) || perm === "ROOT"
          ) ? (
            <Tooltip title={action.name}>
              <Button
                variant="outlined"
                startIcon={action.icon}
                sx={{ m: 1 }}
                onClick={(e) => action.onclick(e, params)}
                color={action.color}
                aria-label={action.label}
              >
                {action.label}
              </Button>
            </Tooltip>
          ) : (
            ""
          )
        );
      },
      width: 250,
    },
  ];

  React.useEffect(() => {
    setLoading(true);
    let cashoutOptions = [];
    axios
      .get(`${process.env.REACT_APP_ACCOUNTS_API_URL}/${endpoint}`)
      .then((res) => {
        const { result } = res.data;
        result.map((row) => {
          cashoutOptions.push(row);
        });
        setCashoutOptions(cashoutOptions);
      })
      .then((_) => setLoading(false));
  }, []);

  return (
    <React.Fragment>
      {openConfirmDialog && (
        <ConfirmDialog
          openConfirmDialog={openConfirmDialog}
          handleOpenConfirmDialog={() => handleOpenConfirmDialog()}
          handleCloseConfirmDialog={handleCloseConfirmDialog}
          dialogHeader={dialogHeader}
          message={confirmMessage}
          actionPresent={actionPresent}
          action={action}
          handler={deleteCashOutOption}
          payload={payload}
        />
      )}
      {openEditDialog && (
        <Edit
          openEditDialog={openEditDialog}
          handleOpenEditDialog={handleOpenEditDialog}
          handleCloseEditDialog={handleCloseEditDialog}
          dialogHeader={editDialogHeader}
          actionPresent={editActionPresent}
          action={action}
          handler={edit}
          param={param}
          row={editRow}
          skipStart={skipStart}
          skipEnd={skipEnd}
          displayStart={displayStart}
          displayEnd={displayEnd}
          url={REACT_APP_ACCOUNTS_API_URL}
          endpoint={endpoint}
          collection={cashoutOptions}
          entity="cashout options"
          setter={setCashoutOptions}
          nonUpdatableFields={nonUpdatableFields}
        />
      )}
      <AddCashOutOption />
      <div style={{ width: "100%" }}>
        <DataGrid
          autoHeight
          rows={cashoutOptions}
          columns={columns}
          rowsPerPageOptions={[20, 50, 100]}
          getRowHeight={() => "auto"}
          disableColumnMenu
          loading={loading}
          components={{ Toolbar: CustomToolbar }}
        />
      </div>
    </React.Fragment>
  );
}
